import { useCallback, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useToast } from '@chakra-ui/toast';
import { useRouter } from 'next/router';

import type {
  FileUploaderRequest,
  FileAttachment,
} from '@/modules/fileUploader/fileUploaderEntity';
import {
  useFileUploader,
  useUploaderBucket,
} from '@/modules/fileUploader/fileUploaderHook';
import { FileUploaderForm } from '@/ui/fileUploader/FileUploaderForm';
import { FileUploaderList } from '@/ui/fileUploader/FileUploaderList';
import { getConfig } from '@/shared/getEnv';
import { localStorageWorker } from '@/shared/storage';

const { publicRuntimeConfig: config } = getConfig();
const localStorageFiles = localStorageWorker.get('files');
const initialFiles = localStorageFiles ? JSON.parse(localStorageFiles) : [];

function FileUploaderContainer() {
  const toast = useToast();
  const { data: buckets = [] } = useUploaderBucket();
  const fileUploader = useFileUploader();

  const router = useRouter();

  const defaultPresetID =
    router.query && router.query.preset
      ? (router.query.preset as string)
      : config.defaultPresetID;

  const [presetID, setPresetID] = useState<string>(defaultPresetID);
  const [files, setFiles] = useState<Array<FileAttachment>>(initialFiles);

  const onUploadFiles = (newFiles: Array<FileAttachment>) => {
    const bucket = buckets.find((b) => b.presetID === presetID);
    if (newFiles.length > 0 && bucket) {
      newFiles.forEach((image) => {
        if (image.file) {
          fileUploader.mutate(
            {
              presetID: bucket.presetID,
              path: bucket.path,
              file: image.file,
              contentType: image.file
                .type as FileUploaderRequest['contentType'],
            },
            {
              onError: (_error) => {
                toast({
                  description: `Sorry, something went wrong. Please try again`,
                  variant: 'subtle',
                  status: 'error',
                  isClosable: true,
                });
              },
              onSuccess: (data) => {
                if (data && data.s3URL) {
                  onSuccessUpload({
                    name: image.name,
                    url: data.s3URL,
                    file: undefined,
                  });
                }
              },
            },
          );
        }
      });
    }
  };

  const onSuccessUpload = (uploadedFiles: FileAttachment) => {
    localStorageWorker.set('files', JSON.stringify([uploadedFiles, ...files]));
    setFiles([uploadedFiles, ...files]);
  };

  const onDeleteFile = useCallback(
    (deletedUrl: string) => {
      const updatedFiles = files.filter((item) => item.url !== deletedUrl);

      localStorageWorker.set('files', JSON.stringify(updatedFiles));
      setFiles(updatedFiles);
    },
    [files],
  );

  return (
    <Stack spacing="3rem">
      <FileUploaderForm
        presetID={presetID}
        isLoading={fileUploader.isLoading}
        onChangePresetID={(presetID) => setPresetID(presetID)}
        onUploadFiles={onUploadFiles}
        selectedBucket={buckets.find((item) => item.presetID === presetID)}
      />
      <FileUploaderList files={files} onDeleteFile={onDeleteFile} />
    </Stack>
  );
}

export { FileUploaderContainer };
